import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  CircularProgress,
  Container,
  Divider,
  Stack,
  Typography,
  Alert,
  TextField,
  Chip,
} from "@mui/material";
import { MdCreditCard, MdPerson, MdWork } from "react-icons/md";
import { themeOrange } from "../../utils/colorTheme";
import CustomSelectBox from "../../component/common/selectComp/CustomSelectBox";
import CustomButton from "../../component/common/Button/Button";
import CustomTextArea from "../../component/common/CustomTextArea/CustomTextArea";
import {
  getUsersWithCardsAsync,
  adminCreateJobAsync,
} from "../../services/UserJobsServices";
import { get_services_list } from "../../services/DashboardServices";
import { get_users_list_by_role } from "../../services/DashboardUsers";
import {
  success_toast_message,
  error_toast_message,
} from "../../utils/toast_message";

const SHIFTS = [
  { value: "Morning", label: "Morning (8AM - 12PM)" },
  { value: "Afternoon", label: "Afternoon (12PM - 5PM)" },
  { value: "Evening", label: "Evening (5PM - 9PM)" },
];

const CreateJob = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [usersWithCards, setUsersWithCards] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [totalCustomers, setTotalCustomers] = useState(0);

  // Form state
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [location, setLocation] = useState("");
  const [description, setDescription] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedShifts, setSelectedShifts] = useState([]);

  // Get services from Redux
  const { get_services_list_data, get_services_list_status } = useSelector(
    (state) => state.dashaboard_services
  );

  useEffect(() => {
    // Fetch customers (role 0) using the working API
    dispatch(get_users_list_by_role(0))
      .unwrap()
      .then((res) => {
        console.log("Users list response:", res);
        // Handle different response formats
        const users = res.users || res.data || res || [];
        if (Array.isArray(users)) {
          setUsersWithCards(users);
          setTotalCustomers(users.length);
        }
        setLoadingUsers(false);
      })
      .catch((err) => {
        console.error("Failed to load users:", err);
        error_toast_message("Failed to load users");
        setLoadingUsers(false);
      });

    // Fetch services
    dispatch(get_services_list());
  }, [dispatch]);

  // Format users for select dropdown
  const userOptions = usersWithCards.map((user) => ({
    value: user.id,
    label: `${user.first_name || ''} ${user.last_name || ''} - ${user.email || ''}`.trim(),
    user: user,
  }));

  // Format services for select dropdown
  const serviceOptions =
    get_services_list_data?.map((service) => ({
      value: service.id,
      label: `${service.name} - $${service.price}`,
      service: service,
    })) || [];

  const handleShiftToggle = (shift) => {
    setSelectedShifts((prev) =>
      prev.includes(shift)
        ? prev.filter((s) => s !== shift)
        : [...prev, shift]
    );
  };

  const handleSubmit = async () => {
    // Validation
    if (!selectedUser) {
      error_toast_message("Please select a client");
      return;
    }
    if (!selectedService) {
      error_toast_message("Please select a service");
      return;
    }
    if (!location.trim()) {
      error_toast_message("Please enter a location");
      return;
    }
    if (!selectedDate) {
      error_toast_message("Please select a date");
      return;
    }
    if (selectedShifts.length === 0) {
      error_toast_message("Please select at least one time slot");
      return;
    }

    setSubmitting(true);

    const jobData = {
      user_id: selectedUser.value,
      service_id: selectedService.value,
      location: location.trim(),
      description: description.trim(),
      date: [
        {
          selected_date: selectedDate,
          shifts: selectedShifts,
        },
      ],
    };

    try {
      console.log("Creating job with data:", jobData);
      const result = await dispatch(adminCreateJobAsync(jobData)).unwrap();
      console.log("Job creation result:", result);

      if (result.status) {
        success_toast_message(result.message || "Job created successfully!");
        navigate("/orders");
      } else {
        error_toast_message(result.message || "Failed to create job");
      }
    } catch (error) {
      console.error("Job creation error:", error);
      const errorMsg = typeof error === 'string' ? error : (error?.message || "Failed to create job");
      error_toast_message(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  // Calculate price
  const calculatePrice = () => {
    if (!selectedService || !selectedUser) return null;

    const service = selectedService.service;
    const user = selectedUser.user;
    const basePrice =
      user.type === "Member" ? service.member_price : service.price;
    const tax = (basePrice / 100) * 13;
    const total = basePrice + tax;

    return { basePrice, tax, total, isMember: user.type === "Member" };
  };

  const priceInfo = calculatePrice();

  return (
    <Stack
      sx={{
        backgroundColor: "#f5f5f5",
        minHeight: "100vh",
        padding: { xs: 2, sm: 3 },
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap={1}
        my={1}
      >
        <Typography
          sx={{
            fontSize: { xs: "20px", sm: "24px" },
            fontWeight: "700",
            color: "#333",
          }}
        >
          Create Job for Client
        </Typography>
      </Stack>
      <Divider />

      <Container
        sx={{
          maxWidth: { xl: "lg", lg: "md", md: "md", sm: "sm", xs: "xs" },
          mt: 3,
        }}
      >
        <Stack
          sx={{
            backgroundColor: "#fff",
            borderRadius: "16px",
            padding: { xs: 2, sm: 4 },
            boxShadow: "0px 4px 20px rgba(0,0,0,0.08)",
          }}
          gap={3}
        >
          {/* Info Alert */}
          <Alert severity="info" icon={<MdCreditCard size={24} />}>
            Select a customer to create a job for. ({totalCustomers} customers available)
          </Alert>

          {/* Select Client */}
          <Stack gap={1}>
            <Stack direction="row" alignItems="center" gap={1}>
              <MdPerson size={20} color={themeOrange} />
              <Typography fontWeight="600">Select Client</Typography>
            </Stack>
            <CustomSelectBox
              options={userOptions}
              value={selectedUser}
              onChange={setSelectedUser}
              placeholder="Search and select a client..."
              isSearchable={true}
              isLoading={loadingUsers}
              noOptionsMessage={() =>
                loadingUsers
                  ? "Loading clients..."
                  : "No clients with cards on file"
              }
            />
            {selectedUser && (
              <Stack
                direction="row"
                alignItems="center"
                gap={1}
                sx={{
                  backgroundColor: "#f0f7ff",
                  padding: "8px 12px",
                  borderRadius: "8px",
                  mt: 1,
                }}
              >
                <Chip
                  label={selectedUser.user.type === "Member" ? "Member" : "Regular"}
                  size="small"
                  sx={{
                    backgroundColor:
                      selectedUser.user.type === "Member" ? "#4CAF50" : "#9e9e9e",
                    color: "white",
                  }}
                />
                <Typography variant="body2" color="textSecondary">
                  {selectedUser.user.phone}
                </Typography>
              </Stack>
            )}
          </Stack>

          {/* Select Service */}
          <Stack gap={1}>
            <Stack direction="row" alignItems="center" gap={1}>
              <MdWork size={20} color={themeOrange} />
              <Typography fontWeight="600">Select Service</Typography>
            </Stack>
            <CustomSelectBox
              options={serviceOptions}
              value={selectedService}
              onChange={setSelectedService}
              placeholder="Select a service..."
              isSearchable={true}
              isLoading={get_services_list_status === "LOADING"}
            />
          </Stack>

          {/* Location */}
          <Stack gap={1}>
            <Typography fontWeight="600">Location / Address</Typography>
            <TextField
              fullWidth
              placeholder="Enter the service location..."
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              variant="outlined"
              size="small"
            />
          </Stack>

          {/* Date */}
          <Stack gap={1}>
            <Typography fontWeight="600">Select Date</Typography>
            <TextField
              type="date"
              fullWidth
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              inputProps={{
                min: new Date().toISOString().split("T")[0],
              }}
              variant="outlined"
              size="small"
            />
          </Stack>

          {/* Time Slots */}
          <Stack gap={1}>
            <Typography fontWeight="600">Select Time Slot(s)</Typography>
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {SHIFTS.map((shift) => (
                <Chip
                  key={shift.value}
                  label={shift.label}
                  onClick={() => handleShiftToggle(shift.value)}
                  sx={{
                    cursor: "pointer",
                    backgroundColor: selectedShifts.includes(shift.value)
                      ? themeOrange
                      : "#f5f5f5",
                    color: selectedShifts.includes(shift.value)
                      ? "white"
                      : "#333",
                    "&:hover": {
                      backgroundColor: selectedShifts.includes(shift.value)
                        ? themeOrange
                        : "#e0e0e0",
                    },
                  }}
                />
              ))}
            </Stack>
          </Stack>

          {/* Description */}
          <Stack gap={1}>
            <Typography fontWeight="600">Job Description (Optional)</Typography>
            <CustomTextArea
              placeholder="Enter job details or special instructions..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
            />
          </Stack>

          {/* Price Summary */}
          {priceInfo && (
            <Stack
              sx={{
                backgroundColor: "#FFF8F5",
                borderRadius: "12px",
                padding: "16px",
                border: `1px solid ${themeOrange}30`,
              }}
              gap={1}
            >
              <Typography fontWeight="600" color={themeOrange}>
                Price Summary
              </Typography>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2">
                  Service Price{" "}
                  {priceInfo.isMember && (
                    <Chip label="Member Rate" size="small" color="success" />
                  )}
                </Typography>
                <Typography variant="body2" fontWeight="600">
                  ${priceInfo.basePrice.toFixed(2)}
                </Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2">HST (13%)</Typography>
                <Typography variant="body2">
                  ${priceInfo.tax.toFixed(2)}
                </Typography>
              </Stack>
              <Divider />
              <Stack direction="row" justifyContent="space-between">
                <Typography fontWeight="700">Total</Typography>
                <Typography fontWeight="700" color={themeOrange}>
                  ${priceInfo.total.toFixed(2)}
                </Typography>
              </Stack>
            </Stack>
          )}

          {/* Submit Button */}
          <Stack direction="row" justifyContent="flex-end" gap={2} mt={2}>
            <CustomButton
              onClick={() => navigate("/orders")}
              style={{
                backgroundColor: "#f5f5f5",
                color: "#333",
                padding: "12px 30px",
                borderRadius: "10px",
              }}
            >
              Cancel
            </CustomButton>
            <CustomButton
              onClick={handleSubmit}
              disabled={submitting}
              style={{
                background:
                  "linear-gradient(180deg, #F15A24 0%, #C53F10 100%)",
                padding: "12px 30px",
                borderRadius: "10px",
              }}
            >
              {submitting ? (
                <CircularProgress size={20} sx={{ color: "white" }} />
              ) : (
                "Create Job"
              )}
            </CustomButton>
          </Stack>
        </Stack>
      </Container>
    </Stack>
  );
};

export default CreateJob;
